import { useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { CONTENT_TYPES } from 'lib/copilotConstants';
import {
    getCopilotStreamAnswerWithFunctions,
    INTERACTION_ITEM_TYPES,
    INTERACTION_TYPES,
    SESSION_TYPES,
} from 'lib/services/copilot';
import { uploadCopilotFile } from 'lib/services/copilotAgents';
import { errorToast } from 'utils/toast';

import { COPILOT_BAR_LOADING_STATES } from './constants';

/**
 * Custom hook for managing copilot chat stream operations
 *
 * This hook provides stream event handlers, chat submission logic, and state management
 * for copilot chat interactions. It handles:
 * - Stream event callbacks (onAnswer, onError, onFinish, etc.)
 * - Chat message submission (text and voice)
 * - Stream cancellation
 * - Loading state management
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.chatId - Unique identifier for the chat session
 * @param {Object} params.session - Session configuration object
 * @param {Function} [params.getInteractions] - Callback function that returns interactions to prepend to prompts
 * @param {Object} params.chat - Current chat state (map of interactions)
 * @param {Object} params.chatSession - Current chat session data
 * @param {Object} params.functionRegistry - Registry of available functions for AI tool calling
 * @param {Function} params.setInteraction - Function to update/create interactions
 * @param {Function} params.setChatSession - Function to update chat session
 * @param {Function} params.setStreamReader - Function to set stream reader
 * @param {Function} params.setCopilotBarProps - Function to update CopilotBar props
 * @param {Function} [params.onAnswerCallback] - Optional callback for answer events
 * @param {Function} [params.onDataCallback] - Optional callback for data events
 * @param {Function} [params.onErrorCallback] - Optional callback for error events
 * @param {Function} [params.onFinishCallback] - Optional callback for finish events
 * @param {Function} [params.onActionCallback] - Optional callback for action events
 * @param {Function} [params.onArtifactCallback] - Optional callback for artifact events
 * @param {Function} [params.onFormCallback] - Optional callback for form events
 * @param {Function} [params.onFormProgressCallback] - Optional callback for form progress events
 * @param {Function} [params.onToolInitCallback] - Optional callback for tool initialization events
 * @param {Function} [params.onToolDoneCallback] - Optional callback for tool completion events
 * @param {Function} [params.debouncedScrollToBottom] - Optional debounced scroll function
 * @param {Function} [params.t] - Optional translation function for voice message label
 *
 * @returns {Object} Chat stream management functions and state
 * @property {boolean} isLoadingAnswer - Whether an answer is currently loading
 * @property {Function} setIsLoadingAnswer - Function to set loading state
 * @property {Object} refs - Object containing refs (answerInteractionIdRef, questionRef, answerRef)
 * @property {Function} onAnswer - Stream answer event handler
 * @property {Function} onData - Stream data event handler
 * @property {Function} onError - Stream error event handler
 * @property {Function} onFinish - Stream finish event handler
 * @property {Function} onAction - Stream action event handler
 * @property {Function} onArtifact - Stream artifact event handler
 * @property {Function} onForm - Stream form event handler
 * @property {Function} onFormProgress - Stream form progress event handler
 * @property {Function} onToolInit - Stream tool initialization event handler
 * @property {Function} onToolDone - Stream tool completion event handler
 * @property {Function} onSetChatSession - Chat session update handler
 * @property {Function} cancelSequence - Cancel current stream sequence
 * @property {Function} submitToChat - Submit message to chat
 * @property {Function} onSubmitChat - Submit text message handler
 * @property {Function} onSubmitVoice - Submit voice message handler
 * @property {Function} getQuestionRef - Get question ref callback
 */
const useChatStream = ({
    chatId,
    session,
    getInteractions,
    chat = {},
    chatSession,
    functionRegistry,
    removeInteraction,
    setInteraction,
    setChatSession,
    setStreamReader,
    setCopilotBarProps,
    onAnswerCallback,
    onDataCallback,
    onErrorCallback,
    onFinishCallback,
    onActionCallback,
    onArtifactCallback,
    onFormCallback,
    onFormProgressCallback,
    onToolInitCallback,
    onToolDoneCallback,
    debouncedScrollToBottom,
    t,
}) => {
    // ========================================
    // REFS
    // ========================================
    const answerInteractionIdRef = useRef(null);
    const lastFinalizedIdRef = useRef(null);
    const questionRef = useRef('');
    const answerRef = useRef('');

    // ========================================
    // LOCAL STATE
    // ========================================
    const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);

    // ========================================
    // STREAM EVENT HANDLERS
    // ========================================
    const onAnswer = useCallback(
        ({ id, responseId, text, delta }) => {
            if (text && chatId) {
                // To keep track of the previous chunk of text
                answerRef.current = text;

                setInteraction({
                    chatId,
                    id: answerInteractionIdRef.current,
                    responseId,
                    text,
                    delta,
                });
                onAnswerCallback?.({ id, responseId, text, delta });
                debouncedScrollToBottom?.(10);
            }
        },
        [chatId, setInteraction, onAnswerCallback, debouncedScrollToBottom],
    );

    const onData = useCallback(
        (data) => {
            onDataCallback?.(data);
        },
        [onDataCallback],
    );

    const onError = useCallback(
        (parentId) => (error) => {
            // Always reset loading state and update UI, even if chatId is falsy
            setIsLoadingAnswer(false);
            setCopilotBarProps?.({ loadingState: COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR });

            // Only perform chatId-dependent operations if chatId is valid
            if (chatId) {
                setStreamReader({ chatId, reader: null });
                setInteraction({
                    chatId,
                    id: answerInteractionIdRef.current,
                    isError: true,
                    parentId,
                });
            }

            answerRef.current = '';
            answerInteractionIdRef.current = null;
            onErrorCallback?.({ parentId, error });
        },
        [chatId, setCopilotBarProps, setInteraction, setStreamReader, onErrorCallback],
    );

    const onFinish = useCallback(
        ({ text }) => {
            if (!chatId) return;
            answerRef.current = '';
            setIsLoadingAnswer(false);
            setCopilotBarProps?.({ loadingState: COPILOT_BAR_LOADING_STATES.COMPLETED });
            setStreamReader({ chatId, reader: null });

            if (answerInteractionIdRef.current) {
                if (text) {
                    // Normal finish — finalize the interaction with text
                    setInteraction({
                        chatId,
                        id: answerInteractionIdRef.current,
                        text,
                        isFinished: true,
                    });
                } else {
                    // Ghost interaction created by the last onTextDone
                    // (no new deltas arrived after it). Remove it cleanly.
                    removeInteraction?.({
                        chatId,
                        id: answerInteractionIdRef.current,
                    });
                }
            }

            // Use the last finalized interaction ID when the ghost was removed
            // (no text means onTextDone already finalized the real interaction)
            const callbackId = text
                ? answerInteractionIdRef.current
                : lastFinalizedIdRef.current || answerInteractionIdRef.current;

            onFinishCallback?.({ id: callbackId, text });

            lastFinalizedIdRef.current = null;
            answerInteractionIdRef.current = null;

            // Scroll to bottom after content is finished loading
            debouncedScrollToBottom?.();
        },
        [
            chatId,
            removeInteraction,
            setCopilotBarProps,
            setInteraction,
            setStreamReader,
            onFinishCallback,
            debouncedScrollToBottom,
        ],
    );

    const onAction = useCallback(
        (action) => {
            if (!chatId) return;
            setInteraction({
                chatId,
                id: answerInteractionIdRef.current,
                action,
            });
            onActionCallback?.(action);
        },
        [chatId, setInteraction, onActionCallback],
    );

    const onArtifact = useCallback(
        ({ type, payload }) => {
            if (!chatId) return;
            setInteraction({
                chatId,
                id: answerInteractionIdRef.current,
                [type]: payload,
            });
            onArtifactCallback?.({ type, payload });
        },
        [chatId, setInteraction, onArtifactCallback],
    );

    const onForm = useCallback(
        (form) => {
            if (!chatId) return;
            setInteraction({ chatId, id: answerInteractionIdRef.current, form });
            onFormCallback?.(form);
        },
        [chatId, setInteraction, onFormCallback],
    );

    const onFormProgress = useCallback(
        (form) => {
            onFormProgressCallback?.(form);
        },
        [onFormProgressCallback],
    );

    const onToolInit = useCallback(
        (tool) => {
            onToolInitCallback?.(tool);
        },
        [onToolInitCallback],
    );

    const onToolDone = useCallback(
        (tool) => {
            onToolDoneCallback?.(tool);
        },
        [onToolDoneCallback],
    );

    /**
     * Handles `response.text.done` events from the stream.
     *
     * When the backend finishes a text segment it fires `response.text.done`,
     * often followed by tool/action/form events and then a new batch of
     * `response.text.delta` events belonging to a different item_id.
     *
     * This handler:
     * 1. Marks the current answer interaction as finished.
     * 2. Creates a new interaction for the next segment so that:
     *    - Loading phrases are visible between segments.
     *    - Actions/forms that arrive before the next text segment
     *      have an interaction to attach to.
     * 3. If no further deltas arrive (last segment), `onFinish`
     *    removes this interaction to avoid a ghost bubble.
     */
    const onTextDone = useCallback(
        ({ text, responseId }) => {
            if (!chatId || !answerInteractionIdRef.current) return;

            // Finalise the current answer interaction
            setInteraction({
                chatId,
                id: answerInteractionIdRef.current,
                text,
                responseId,
                isFinished: true,
            });

            // Track the finalized interaction so onFinish can reference it
            // after the ghost is removed
            lastFinalizedIdRef.current = answerInteractionIdRef.current;

            // Prepare a new interaction for the next text segment
            answerRef.current = '';
            answerInteractionIdRef.current = uuid();

            setInteraction({
                chatId,
                id: answerInteractionIdRef.current,
                text: '',
                isLoading: true,
            });

            debouncedScrollToBottom?.();
        },
        [chatId, setInteraction, debouncedScrollToBottom],
    );

    const onSetChatSession = useCallback(
        (sessionData) => {
            if (!chatId) return;
            setChatSession({ chatId, session: sessionData });
        },
        [chatId, setChatSession],
    );

    const cancelSequence = useCallback(() => {
        if (!chatId) return;
        setStreamReader({ chatId, reader: null });
    }, [chatId, setStreamReader]);

    // ========================================
    // CHAT ACTIONS
    // ========================================
    /**
     * Shared submit logic for both text and voice inputs
     * @param {Object} options - Submit options
     * @param {string} options.questionText - Display text for the question interaction
     * @param {Array} options.newInteractions - Interactions to append (text message or audio batches)
     * @param {Object} [options.sessionOverrides] - Additional session configuration
     * @param {string} options.errorLogPrefix - Prefix for error console log
     * @param {Object} [options.questionMetadata] - Additional metadata for the question interaction (e.g., audioBatches)
     */
    const submitToChat = useCallback(
        ({
            errorLogPrefix,
            newInteractions,
            questionMetadata = {},
            questionText,
            sessionOverrides = {},
        }) => {
            if (!chatId || !session) return Promise.resolve();

            cancelSequence();
            setIsLoadingAnswer(true);
            setCopilotBarProps?.({
                feedback: null,
                loadingPhrases: [],
                loadingState: COPILOT_BAR_LOADING_STATES.LOADING,
            });
            const questionId = uuid();

            answerInteractionIdRef.current = uuid();

            setInteraction({
                chatId,
                id: questionId,
                isQuestion: true,
                text: questionText,
                ...questionMetadata,
            });

            setInteraction({
                chatId,
                id: answerInteractionIdRef.current,
                text: '',
                isLoading: true,
                parentId: questionId,
            });

            // Scroll to bottom after setting up the interaction
            debouncedScrollToBottom?.();

            // Get interactions to prepend from callback
            const prependedInteractions = getInteractions?.() || [];

            // Determine session type: UPDATE only if session ID exists AND there are existing completed interactions
            // Check for existing interactions in:
            // 1. The chat state (completed interactions already in the conversation, excluding the ones we just added)
            // Note: We check for completed interactions (isFinished: true) to ensure we're detecting a real conversation continuation
            // If there are no completed interactions, always use CREATE (even if chatSession?.id exists)
            const existingChatKeys = Object.keys(chat).filter(
                (key) =>
                    key !== questionId &&
                    key !== answerInteractionIdRef.current &&
                    chat[key]?.isFinished === true,
            );
            const hasExistingInteractions = existingChatKeys.length > 0;
            const sessionType =
                chatSession?.id && hasExistingInteractions
                    ? SESSION_TYPES.UPDATE
                    : SESSION_TYPES.CREATE;

            return getCopilotStreamAnswerWithFunctions({
                session: {
                    ...session,
                    id: chatSession?.id,
                    type: sessionType,
                    ...sessionOverrides,
                },
                interactions: [...prependedInteractions, ...newInteractions],
                functionRegistry,
                maxFunctionCalls: 10,
                setText: onAnswer,
                setData: onData,
                setSession: onSetChatSession,
                onFinish,
                onError: onError(questionId),
                onAction,
                onSetReader: (reader) => setStreamReader({ chatId, reader }),
                onArtifact,
                onForm,
                onFormProgress,
                onToolInit,
                onToolDone,
                onTextDone,
            }).catch((error) => {
                console.error(`${errorLogPrefix}:`, error);
                setIsLoadingAnswer(false);
                setCopilotBarProps?.({ loadingState: COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR });
                setStreamReader({ chatId, reader: null });
                setInteraction({
                    chatId,
                    id: answerInteractionIdRef.current,
                    isError: true,
                    parentId: questionId,
                });
            });
        },
        [
            cancelSequence,
            chat,
            chatId,
            chatSession?.id,
            debouncedScrollToBottom,
            functionRegistry,
            getInteractions,
            onAction,
            onAnswer,
            onArtifact,
            onData,
            onError,
            onFinish,
            onForm,
            onFormProgress,
            onSetChatSession,
            onTextDone,
            onToolDone,
            onToolInit,
            session,
            setCopilotBarProps,
            setInteraction,
            setStreamReader,
        ],
    );

    const onSubmitChat = useCallback(
        (suggestion) => {
            if (!chatId || !session) return;
            setCopilotBarProps?.({ loadingState: COPILOT_BAR_LOADING_STATES.IDLE });
            const question = suggestion || questionRef.current || '';

            return submitToChat({
                questionText: question,
                newInteractions: [
                    {
                        item: {
                            type: INTERACTION_TYPES.MESSAGE,
                            content: { type: CONTENT_TYPES.INPUT_TEXT, text: question },
                        },
                    },
                ],
                errorLogPrefix: 'Chat error',
            });
        },
        [chatId, session, setCopilotBarProps, submitToChat],
    );

    const onSubmitVoice = useCallback(
        (audioData) => {
            if (
                !chatId ||
                !session ||
                !audioData ||
                !audioData.batches ||
                audioData.batches.length === 0
            )
                return;

            setCopilotBarProps?.({ loadingState: COPILOT_BAR_LOADING_STATES.IDLE });
            const { batches, compressedAudio, audioFormat } = audioData;
            const eventId = uuid();

            let audioInteractions;
            let inputAudioFormat;

            // Use compressed audio if available (M4A or WebM), otherwise fall back to PCM16 batches
            if (compressedAudio && audioFormat) {
                // Compressed audio: send as single blob
                inputAudioFormat = audioFormat; // 'm4a' or 'webm'
                audioInteractions = [
                    {
                        audio: compressedAudio,
                        event_id: eventId,
                        type: INTERACTION_ITEM_TYPES.INPUT_AUDIO_BUFFER_APPEND,
                    },
                    {
                        event_id: eventId,
                        type: INTERACTION_ITEM_TYPES.INPUT_AUDIO_BUFFER_COMMIT,
                    },
                ];
            } else {
                // Fallback: PCM16 batches (multiple appends)
                inputAudioFormat = 'pcm16';
                audioInteractions = batches.map((batch) => ({
                    audio: batch.audio,
                    event_id: eventId,
                    type: INTERACTION_ITEM_TYPES.INPUT_AUDIO_BUFFER_APPEND,
                }));
                audioInteractions.push({
                    event_id: eventId,
                    type: INTERACTION_ITEM_TYPES.INPUT_AUDIO_BUFFER_COMMIT,
                });
            }

            return submitToChat({
                errorLogPrefix: 'Voice chat error',
                newInteractions: audioInteractions,
                questionMetadata: { audioData },
                questionText: t?.('label_voice_message') || '🎤 Voice message',
                sessionOverrides: { inputAudioFormat },
            });
        },
        [chatId, session, setCopilotBarProps, submitToChat, t],
    );

    /**
     * Upload files to S3 and submit them for processing by the Copilot backend
     * @param {Array<File>} files - Array of files to upload and submit
     * @param {string} [prompt] - Optional text prompt to include with the files
     * @returns {Promise} Promise that resolves when submission is complete
     */
    const onSubmitFiles = useCallback(
        (files, prompt) => {
            if (!chatId || !session || !files?.length) return Promise.resolve();

            setCopilotBarProps?.({
                loadingState: COPILOT_BAR_LOADING_STATES.IDLE,
                uploadingFiles: true,
            });

            return Promise.all(files.map((file) => uploadCopilotFile(file)))
                .then((s3Keys) => {
                    setCopilotBarProps?.({ uploadingFiles: false });

                    const fileInteractions = s3Keys.map((id) => ({
                        item: {
                            type: INTERACTION_TYPES.MESSAGE,
                            content: { type: CONTENT_TYPES.INPUT_FILE, id },
                        },
                    }));

                    // If a text prompt is provided, append it as a text interaction
                    const interactions = prompt
                        ? [
                              ...fileInteractions,
                              {
                                  item: {
                                      type: INTERACTION_TYPES.MESSAGE,
                                      content: { type: CONTENT_TYPES.INPUT_TEXT, text: prompt },
                                  },
                              },
                          ]
                        : fileInteractions;

                    // Build display text for the question
                    const fileNames = files.map((f) => f.name).join(', ');
                    const questionText = prompt
                        ? `${prompt} (${fileNames})`
                        : t?.('label_file_upload') || fileNames;

                    return submitToChat({
                        errorLogPrefix: 'File upload error',
                        newInteractions: interactions,
                        questionText,
                    });
                })
                .catch((error) => {
                    console.error('File upload error:', error);
                    setCopilotBarProps?.({
                        loadingState: COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR,
                        uploadingFiles: false,
                    });
                    errorToast({ t, title: t('cfm_label_error_generic') });
                    throw error;
                });
        },
        [chatId, session, setCopilotBarProps, submitToChat, t],
    );

    const getQuestionRef = useCallback((question) => {
        questionRef.current = question;
    }, []);

    return {
        isLoadingAnswer,
        setIsLoadingAnswer,
        refs: {
            answerInteractionIdRef,
            questionRef,
            answerRef,
        },
        onAnswer,
        onData,
        onError,
        onFinish,
        onAction,
        onArtifact,
        onForm,
        onFormProgress,
        onToolInit,
        onToolDone,
        onTextDone,
        onSetChatSession,
        cancelSequence,
        submitToChat,
        onSubmitChat,
        onSubmitFiles,
        onSubmitVoice,
        getQuestionRef,
    };
};

export default useChatStream;
